import type { QueryClient } from '@tanstack/react-query';
import {
  TECH_FLAG_DEFAULT_KIND,
  TECH_FLAG_KINDS,
  type TechFlagKind,
} from '@servwell/shared';
import { pathOf } from './offline-replay';
import { draftKey } from './drafts';
import { restoreDraft } from './useDraft';
import type { PendingWrites } from './pending-writes';
import type { FailedWriteLog } from './failed-writes';
import type { QueuedUpload, UploadQueue } from './upload-queue';

/**
 * A ticket's photos follow the flag that creates the ticket (ops insights Phase 5a, ticket 03):
 * "Flag a problem" on the tech's stop sends a `ticket.flag` with a ticket id minted on the
 * phone, and the photos taken with it are queued against that id. This file is everything the
 * photo queue (`upload-queue.ts`, which knows nothing of tickets) does about it:
 *
 *  - **Held** while the flag is still on the phone, or waits refused in the changes panel
 *    (`ticketPhotoAwaitsItsFlag`, the queue's `awaiting` rule). The server answers "not now" to
 *    a tech's photo for any ticket they cannot attach to (one answer, so it confirms no id),
 *    and the queue asks again only while that is so; otherwise it gives the photo up as never
 *    sent after a few tries (`NEVER_SENT`).
 *  - **Sent** as soon as the flag lands, by any route (the queue drains on it).
 *  - **Given back** when the flag will not be delivered from this phone: refused before it was
 *    kept, or dismissed from the panel. The flag goes back into its visit's draft, and its
 *    photos are parked with it (`giveFlagBack`): Send releases them again, Discard drops them.
 */

export const TICKET_FLAG = 'ticket.flag';

/** What a ticket photo that will never have its ticket is marked with. */
export const NEVER_SENT = 'The problem this photo was for was never sent.';

/** What "Flag a problem" keeps on the phone while the tech is writing it. */
export type FlagDraft = {
  /**
   * The ticket's id, minted on THIS phone at the first keystroke or photo (an untouched form
   * keeps no draft). Its photos are queued against it and the flag is sent with it.
   */
  ticketId: string;
  title: string;
  body: string;
  /**
   * What kind of problem (Phase 6a): the ticket is filed under it. A draft kept before the kind
   * existed has none; read it with `flagKindOf`.
   */
  category: TechFlagKind;
};

export const EMPTY_FLAG_DRAFT: FlagDraft = {
  ticketId: '',
  title: '',
  body: '',
  category: TECH_FLAG_DEFAULT_KIND,
};

/**
 * The kind a draft or a flag's variables name, read off what the phone kept: one of the four,
 * or Other for anything else (a draft or a queued flag from before the kind existed).
 */
export const flagKindOf = (value: unknown): TechFlagKind =>
  (TECH_FLAG_KINDS as readonly unknown[]).includes(value)
    ? (value as TechFlagKind)
    : TECH_FLAG_DEFAULT_KIND;

/** A slot with no draft in it: nothing typed and no photo taken (no id minted). */
export const isFreeFlagDraft = (current: unknown): boolean =>
  !(current as Partial<FlagDraft> | null | undefined)?.ticketId;

/**
 * Must this queued photo wait? A ticket's, while its `ticket.flag` is on the phone (which
 * `PendingWrites.awaits` also says until the queued writes have been read, so the order holds
 * across a restart) or waits refused in the panel to be retried. Every other photo goes.
 */
export function ticketPhotoAwaitsItsFlag(
  item: Pick<QueuedUpload, 'entityType' | 'entityId'>,
  writes: Pick<PendingWrites, 'awaits'>,
  log: Pick<FailedWriteLog, 'holds'>,
): boolean {
  return (
    item.entityType === 'ticket' &&
    (writes.awaits(TICKET_FLAG, 'ticketId', item.entityId) ||
      log.holds(TICKET_FLAG, 'ticketId', item.entityId))
  );
}

/**
 * A flag that will not be delivered from this phone goes back to its visit's draft, and its
 * photos are parked again with it. If the tech has started another flag there meanwhile, that
 * draft is kept and the photos join it (parked, shown in its sheet, removable): nothing is
 * thrown away that a person did not throw away.
 */
export async function giveFlagBack(
  variables: unknown,
  userId: string,
  queue: Pick<UploadQueue, 'park'>,
  restore: typeof restoreDraft = restoreDraft,
): Promise<void> {
  const v = variables as {
    ticketId?: unknown;
    visitId?: unknown;
    title?: unknown;
    message?: unknown;
    category?: unknown;
  } | null;
  if (typeof v?.ticketId !== 'string' || typeof v.visitId !== 'string') return;
  const back: FlagDraft = {
    ticketId: v.ticketId,
    title: typeof v.title === 'string' ? v.title : '',
    body: typeof v.message === 'string' ? v.message : '',
    // A refused "Missing or wrong part" comes back as that, not as Other.
    category: flagKindOf(v.category),
  };
  const now = (await restore(
    draftKey('ticket-flag', userId, v.visitId),
    back,
    isFreeFlagDraft,
  )) as Partial<FlagDraft> | undefined;
  await queue.park('ticket', back.ticketId, now?.ticketId || back.ticketId);
}

/**
 * A flag the screen just saw fail: given back unless it is still kept on the phone (a 401 or a
 * parked send leaves its row) or waits refused in the panel (the panel is where it goes from
 * there). What is left is a flag refused before it was kept (no room on the phone, no key, nobody
 * signed in): it would otherwise vanish with its draft already cleared.
 */
export function giveBackUnlessKept(
  variables: { ticketId: string },
  userId: string,
  deps: {
    writes: Pick<PendingWrites, 'awaits'>;
    log: Pick<FailedWriteLog, 'holds'>;
    queue: Pick<UploadQueue, 'park'>;
  },
): Promise<void> {
  if (
    deps.writes.awaits(TICKET_FLAG, 'ticketId', variables.ticketId) ||
    deps.log.holds(TICKET_FLAG, 'ticketId', variables.ticketId)
  ) {
    return Promise.resolve();
  }
  return giveFlagBack(variables, userId, deps.queue);
}

/**
 * Start following flags for the app's one photo queue: the hold rule, a drain whenever a flag
 * lands (from the queue, a screen, or a retry from the panel), and giving back a flag a person
 * lets go of in the panel. Called once, where the queue is started (ApiProvider).
 */
export function startTicketPhotos(deps: {
  queue: UploadQueue;
  queryClient: QueryClient;
  writes: Pick<PendingWrites, 'awaits'>;
  log: FailedWriteLog;
}): () => void {
  const { queue, queryClient, writes, log } = deps;
  /**
   * Tickets whose flag is being given back right now: their photos stay held until they are
   * parked. The panel's dismissal takes the flag out of the log in the same breath, and the
   * drain that hears it must not send what is about to be parked.
   */
  const givingBack = new Set<string>();
  queue.holdWith({
    awaiting: (item) =>
      ticketPhotoAwaitsItsFlag(item, writes, log) ||
      (item.entityType === 'ticket' && givingBack.has(item.entityId)),
    neverReady: NEVER_SENT,
  });
  const stopLanding = queryClient.getMutationCache().subscribe((event) => {
    if (
      event.type === 'updated' &&
      event.action.type === 'success' &&
      pathOf(event.mutation.options.mutationKey) === TICKET_FLAG
    ) {
      void queue.drain();
    }
  });
  // A retry from the panel lands outside the mutation cache; the log changes when it does.
  const stopLog = log.subscribe(() => void queue.drain());
  const stopDiscard = log.onDiscard((row) => {
    const ticketId = (row.variables as { ticketId?: unknown }).ticketId;
    if (row.path === TICKET_FLAG && row.userId && typeof ticketId === 'string') {
      givingBack.add(ticketId);
      void giveFlagBack(row.variables, row.userId, queue).finally(() =>
        givingBack.delete(ticketId),
      );
    }
  });
  return () => {
    stopLanding();
    stopLog();
    stopDiscard();
  };
}
